import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import RoleAppHeader from "../components/RoleAppHeader.jsx";
import ListingPricingPanel from "../components/programs/ListingPricingPanel.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { useRole } from "../hooks/useRole.js";
import { useStripeConnect } from "../hooks/useStripeConnect.js";
import { getClientProgramView, getCoachListingById } from "../lib/appwrite.js";
import {
    isListingPricingComplete,
    isListingPublished,
    priceLabelForListing,
} from "../lib/listingPricing.js";
import {
    ProgramDetailSkeleton,
    SidebarActionSkeleton,
} from "../components/loading/SkeletonBlocks.jsx";
import { createListingCheckoutSession, cancelListingSubscription, fetchListingAccess, syncListingCheckoutSessionWithRetry } from "../lib/stripe.js";
import { formatBillingInstant } from "../lib/stripeInstant.js";

const DEFAULT_COVER =
    "linear-gradient(135deg, rgba(255,118,25,0.55) 0%, rgba(245,158,11,0.45) 100%)";

export default function ProgramPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const role = useRole();
    const { user } = useAuth();
    const { connect: stripeConnect } = useStripeConnect();
    const [searchParams] = useSearchParams();
    const justPurchased = searchParams.get("purchased") === "1";
    const checkoutSessionId = searchParams.get("session_id")?.trim() ?? "";

    const [program, setProgram] = useState(null);
    const [programLoading, setProgramLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [listingAccess, setListingAccess] = useState(null);
    const [accessChecking, setAccessChecking] = useState(false);
    const [purchasing, setPurchasing] = useState(false);
    const [checkoutError, setCheckoutError] = useState(null);
    const [cancelBusy, setCancelBusy] = useState(false);
    const [cancelError, setCancelError] = useState(null);

    useEffect(() => {
        if (role === "loading") return;

        let cancelled = false;
        setProgramLoading(true);

        (async () => {
            try {
                let found = null;
                if (role === "coach" && user?.$id) {
                    found = await getCoachListingById(user.$id, id);
                } else if (role !== "coach") {
                    found = await getClientProgramView(id, user?.$id);
                }
                if (!cancelled) setProgram(found);
            } catch {
                if (!cancelled) setProgram(null);
            } finally {
                if (!cancelled) setProgramLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [id, role, user?.$id]);

    useEffect(() => {
        if (role !== "client" || program == null || program.viewType === "assigned") {
            setListingAccess(null);
            return;
        }
        let cancelled = false;
        setAccessChecking(true);
        (async () => {
            if (justPurchased && checkoutSessionId) {
                try {
                    const synced = await syncListingCheckoutSessionWithRetry(
                        checkoutSessionId,
                        program.id,
                        { retries: 5, baseDelayMs: 900 },
                    );
                    if (cancelled) return;
                    if (synced?.owned) {
                        setListingAccess(synced);
                        setAccessChecking(false);
                        return;
                    }
                } catch {
                    // Fall back to listing-access polling below.
                }
            }

            const attempts = justPurchased ? 8 : 1;
            for (let i = 0; i < attempts; i++) {
                try {
                    const data = await fetchListingAccess(program.id);
                    if (cancelled) return;
                    setListingAccess(data);
                    if (data?.owned) {
                        setAccessChecking(false);
                        return;
                    }
                } catch {
                    // ignore and retry
                }
                if (i < attempts - 1) {
                    await new Promise((r) => setTimeout(r, 1500));
                }
            }
            if (!cancelled) {
                setListingAccess((prev) => prev ?? { owned: false, purchaseType: null, subscription: null });
                setAccessChecking(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [role, program, justPurchased, checkoutSessionId]);

    async function handleCancelSubscription() {
        if (!program?.id || cancelBusy) return;
        if (
            !window.confirm(
                "Cancel your subscription? You'll keep access until the end of the current billing period.",
            )
        ) {
            return;
        }
        setCancelBusy(true);
        setCancelError(null);
        try {
            const data = await cancelListingSubscription(program.id);
            setListingAccess((prev) => ({
                ...prev,
                owned: true,
                purchaseType: "subscription",
                subscription: data.subscription ?? prev?.subscription,
            }));
        } catch (e) {
            setCancelError(e?.message ?? "Could not cancel subscription.");
        } finally {
            setCancelBusy(false);
        }
    }

    const ownedListing = listingAccess?.owned === true;
    const listingSubscription = listingAccess?.subscription ?? null;

    function handleCopyLink() {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    async function handleGetProgram() {
        if (role === "unauthenticated" || role === "loading") {
            navigate("/login", {
                state: { from: { pathname: `/programs/${id}` } },
            });
            return;
        }
        if (purchasing) return;

        setPurchasing(true);
        setCheckoutError(null);
        try {
            const { url } = await createListingCheckoutSession(program.id);
            if (!url) throw new Error("No checkout URL returned.");
            window.location.href = url;
        } catch (e) {
            setCheckoutError(e?.message ?? "Could not start checkout.");
            setPurchasing(false);
        }
    }

    if (programLoading) {
        return (
            <div className="hc-page-shell min-h-screen">
                <RoleAppHeader />
                <ProgramDetailSkeleton />
            </div>
        );
    }

    if (!program) {
        return (
            <div className="hc-page-shell min-h-screen">
                <RoleAppHeader />
                <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
                    <div className="flex size-14 items-center justify-center rounded-full border border-hc-surface-border bg-hc-surface">
                        <i className="icon-exclamation text-xl leading-none text-hc-text-dim" aria-hidden="true" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold tracking-[-0.44px] text-hc-text">
                            Program not found
                        </h1>
                        <p className="mt-1.5 text-sm text-hc-text-dim">
                            This program doesn't exist or hasn't been published yet.
                        </p>
                    </div>
                    <Link to="/" className="text-sm text-hc-primary hover:underline">
                        Go home
                    </Link>
                </div>
            </div>
        );
    }

    const isAssignedProgram = program.viewType === "assigned";
    const priceLabel = priceLabelForListing(program);
    const isSubscription = program.pricingMode === "subscription";
    const canBuy = isListingPricingComplete(program);
    const canShare = isListingPricingComplete(program);
    const metaParts = [
        program.category,
        program.level,
        program.durationWeeks ? `${program.durationWeeks} weeks` : "",
    ].filter(Boolean);

    return (
        <div className="hc-page-shell min-h-screen">
            <RoleAppHeader />

            <div className="relative mx-auto max-w-5xl px-6 pb-16 pt-10">
                <div
                    className="pointer-events-none absolute right-[10%] top-20 size-[280px] rounded-full bg-hc-warm/10 blur-[64px]"
                    aria-hidden
                />
                <div
                    className="pointer-events-none absolute bottom-[20%] left-[5%] size-[240px] rounded-full bg-hc-secondary/15 blur-[64px]"
                    aria-hidden
                />

                <div className="relative z-10 grid gap-8 lg:grid-cols-[1fr_300px]">
                    <div>
                        <div
                            className="mb-6 h-44 w-full rounded-2xl"
                            style={{ background: DEFAULT_COVER }}
                        />

                        <div className="mb-3 flex flex-wrap items-center gap-2">
                            {isAssignedProgram ? (
                                <span className="rounded-full border border-hc-secondary/35 bg-hc-secondary/10 px-2.5 py-0.5 text-xs font-medium text-hc-secondary">
                                    Coach program
                                </span>
                            ) : (
                                <>
                                    <span className="rounded-full border border-hc-primary/35 bg-hc-primary/10 px-2.5 py-0.5 text-xs font-medium text-hc-primary">
                                        {priceLabel}
                                    </span>
                                    <span className="rounded-full border border-hc-surface-border bg-hc-surface px-2.5 py-0.5 text-xs text-hc-text-dim">
                                        Shareable program
                                    </span>
                                </>
                            )}
                            {role === "coach" && !isListingPublished(program) && (
                                <span className="rounded-full border border-hc-surface-border-strong bg-hc-surface-strong px-2.5 py-0.5 text-xs text-hc-text-dim">
                                    Draft
                                </span>
                            )}
                        </div>

                        <h1 className="text-3xl font-extrabold tracking-[-0.44px] text-hc-text">
                            {program.title}
                        </h1>
                        {program.coachName && (
                            <p className="mt-1.5 text-sm text-hc-text-dim">
                                by{" "}
                                <span className="font-medium text-hc-text">
                                    {program.coachName}
                                </span>
                            </p>
                        )}

                        {metaParts.length > 0 && (
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-hc-text-dim">
                                {metaParts.map((part) => (
                                    <span
                                        key={part}
                                        className="rounded-full border border-hc-surface-border bg-hc-surface px-2.5 py-0.5 capitalize"
                                    >
                                        {part}
                                    </span>
                                ))}
                            </div>
                        )}

                        {program.tagline && (
                            <p className="mt-5 text-sm font-medium leading-6 text-hc-text">
                                {program.tagline}
                            </p>
                        )}

                        <p className="mt-3 whitespace-pre-line text-sm leading-6 text-hc-text-muted">
                            {program.description}
                        </p>
                    </div>

                    <div className="lg:sticky lg:top-24 lg:self-start">
                        <div className="rounded-2xl border border-hc-surface-border-strong bg-hc-surface p-6 shadow-[0px_25px_50px_0px_rgba(0,0,0,0.25)]">
                                {!isAssignedProgram && (
                                    <>
                                        <div className="flex items-baseline gap-1.5">
                                            <span className="text-3xl font-extrabold tracking-[-0.44px] text-hc-text">
                                                {priceLabel}
                                            </span>
                                            {canBuy && (
                                                <span className="text-xs text-hc-text-dim">
                                                    {isSubscription ? "per month" : "one-time"}
                                                </span>
                                            )}
                                        </div>
                                        {canBuy && (
                                            <p className="mt-1 text-xs text-hc-text-dim">
                                                {isSubscription
                                                    ? "Subscription · Cancel anytime"
                                                    : "One-time purchase · Yours to keep"}
                                            </p>
                                        )}
                                    </>
                                )}

                                {role === "loading" ? (
                                    <SidebarActionSkeleton />
                                ) : role === "coach" ? (
                                    <>
                                        <div className="mt-5 border-t border-hc-surface-border pt-5">
                                            <ListingPricingPanel
                                                listing={program}
                                                connectReady={Boolean(stripeConnect?.ready)}
                                                onSaved={async (updated) => {
                                                    setProgram((prev) => ({ ...prev, ...updated }));
                                                    if (user?.$id) {
                                                        const fresh = await getCoachListingById(
                                                            user.$id,
                                                            id,
                                                        );
                                                        if (fresh) setProgram(fresh);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleCopyLink}
                                            disabled={!canShare}
                                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-[14px] border border-hc-surface-border-strong bg-white/5 px-4 py-3.5 text-sm font-semibold text-hc-text transition hover:bg-white/10 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <i
                                                className={`${copied ? "icon-check" : "icon-link"} leading-none`}
                                                aria-hidden="true"
                                            />
                                            {copied ? "Link copied!" : "Copy link"}
                                        </button>
                                    </>
                                ) : isAssignedProgram ? (
                                    <div className="flex flex-col gap-2 rounded-[12px] border border-hc-secondary/30 bg-hc-secondary/10 px-3 py-3">
                                        <div className="flex items-start gap-2">
                                            <i
                                                className="icon-check-circle shrink-0 leading-none text-hc-secondary"
                                                aria-hidden="true"
                                            />
                                            <div>
                                                <p className="text-xs font-semibold text-hc-text">
                                                    Assigned by your coach
                                                </p>
                                                <p className="mt-1 text-xs text-hc-text-dim">
                                                    Open the HeyCoach app to follow this program.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : accessChecking ? (
                                    <SidebarActionSkeleton />
                                ) : ownedListing ? (
                                    <>
                                        <div
                                            className={`mt-4 flex flex-col gap-2 rounded-[12px] border px-3 py-3 ${justPurchased
                                                ? "border-hc-success/30 bg-hc-success-muted"
                                                : "border-hc-surface-border-strong bg-hc-surface-strong"
                                                }`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <i
                                                    className={`icon-check-circle shrink-0 leading-none ${justPurchased ? "text-hc-success" : "text-hc-text-dim"}`}
                                                    aria-hidden="true"
                                                />
                                                <div>
                                                    <p
                                                        className={`text-xs font-semibold ${justPurchased ? "text-hc-success" : "text-hc-text"}`}
                                                    >
                                                        {justPurchased
                                                            ? "Purchase complete"
                                                            : "You own this program"}
                                                    </p>
                                                    <p className="mt-1 text-xs text-hc-text-dim">
                                                        {justPurchased
                                                            ? "Open the HeyCoach app to start this program."
                                                            : "Open the HeyCoach app to view this program."}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        {listingAccess?.purchaseType === "subscription" && listingSubscription && (
                                            <div className="mt-4 space-y-2">
                                                {listingSubscription.cancelAtPeriodEnd ? (
                                                    <p className="rounded-[12px] border border-hc-surface-border bg-hc-surface-strong px-3 py-2.5 text-xs text-hc-text-dim">
                                                        Subscription cancels
                                                        {listingSubscription.currentPeriodEnd
                                                            ? ` on ${formatBillingInstant(listingSubscription.currentPeriodEnd)}`
                                                            : " at period end"}
                                                        . You keep access until then.
                                                    </p>
                                                ) : (
                                                    <>
                                                        {cancelError && (
                                                            <p className="text-xs text-hc-error">{cancelError}</p>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={handleCancelSubscription}
                                                            disabled={cancelBusy}
                                                            className="w-full rounded-[14px] border border-hc-surface-border-strong bg-white/5 px-4 py-3 text-sm font-medium text-hc-text-muted transition hover:bg-white/10 hover:text-hc-text disabled:opacity-60"
                                                        >
                                                            {cancelBusy ? "Cancelling…" : "Cancel subscription"}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : canBuy ? (
                                    <>
                                        {checkoutError && (
                                            <p className="mt-3 text-xs text-hc-error">{checkoutError}</p>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleGetProgram}
                                            disabled={purchasing}
                                            className="mt-5 flex w-full items-center justify-center gap-2 rounded-[14px] px-4 py-3.5 text-sm font-semibold text-white transition hc-brand-gradient hc-shadow-brand hover:brightness-110 active:scale-95 disabled:opacity-60"
                                        >
                                            {purchasing ? "Redirecting to checkout…" : "Get this program"}
                                            {!purchasing && (
                                                <i className="icon-arrow-right leading-none" aria-hidden="true" />
                                            )}
                                        </button>
                                        {role === "unauthenticated" && (
                                            <p className="mt-3 text-center text-xs text-hc-text-dim">
                                                You'll need an account to purchase
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <p className="mt-5 rounded-[12px] border border-hc-surface-border bg-hc-surface-strong px-3 py-3 text-center text-xs text-hc-text-dim">
                                        This program isn't available for purchase right now.
                                    </p>
                                )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
